import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { Bell, Inbox, Building2, Info, CheckCheck, X, Sparkles, Upload, Briefcase } from "lucide-react";

const BASE = import.meta.env.BASE_URL;

const BUCKETS = [
  { key: "for_you", label: "For you", desc: "Decisions, replies, tasks waiting on you", Icon: Inbox },
  { key: "your_portcos", label: "Your portcos", desc: "Activity on companies you cover", Icon: Building2 },
  { key: "fyi", label: "FYI", desc: "Cohort-wide announcements and digest pings", Icon: Info },
] as const;

function kindIcon(kind: string) {
  if (kind.startsWith("viren_")) return Sparkles;
  if (kind === "artifact_uploaded") return Upload;
  if (kind === "reallocation_decision") return Briefcase;
  return Bell;
}

export default function BankerInbox() {
  const [, navigate] = useLocation();
  const [bucket, setBucket] = useState<string>("for_you");
  const [items, setItems] = useState<any[]>([]);
  const [counts, setCounts] = useState<any>({ for_you: 0, your_portcos: 0, fyi: 0, total: 0 });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const token = localStorage.getItem("banker_token");
    if (!token) return;
    setLoading(true);
    try {
      const [r, rc] = await Promise.all([
        fetch(`${BASE}api/banker/me/notifications?bucket=${bucket}`, { headers: { "x-banker-token": token } }),
        fetch(`${BASE}api/banker/me/notifications/counts`, { headers: { "x-banker-token": token } }),
      ]);
      if (r.ok) setItems(await r.json());
      if (rc.ok) setCounts(await rc.json());
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [bucket]);

  const setRead = async (id: number, read: boolean) => {
    const token = localStorage.getItem("banker_token") || "";
    await fetch(`${BASE}api/banker/me/notifications/${id}`, {
      method: "PATCH", headers: { "Content-Type": "application/json", "x-banker-token": token },
      body: JSON.stringify({ read }),
    });
    await load();
  };
  const archive = async (id: number) => {
    const token = localStorage.getItem("banker_token") || "";
    await fetch(`${BASE}api/banker/me/notifications/${id}`, {
      method: "PATCH", headers: { "Content-Type": "application/json", "x-banker-token": token },
      body: JSON.stringify({ archived: true }),
    });
    await load();
  };
  const markAllRead = async () => {
    const token = localStorage.getItem("banker_token") || "";
    await fetch(`${BASE}api/banker/me/notifications/mark-all-read`, {
      method: "POST", headers: { "Content-Type": "application/json", "x-banker-token": token },
      body: JSON.stringify({ bucket }),
    });
    await load();
  };

  const open = async (n: any) => {
    if (!n.readAt) await setRead(n.id, true);
    if (n.link) navigate(n.link);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-2"><Bell className="w-7 h-7 text-[#FF5C00]" /> Inbox</h1>
          <p className="text-white/50 text-sm mt-1">Everything that needs your eyes — sorted by what's actually for you, what's on your portcos, and what's just FYI.</p>
        </div>
        <button onClick={markAllRead} className="bg-white/[0.06] hover:bg-white/[0.1] border border-white/15 px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 text-white/80">
          <CheckCheck className="w-3.5 h-3.5" /> Mark all read
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {BUCKETS.map(b => {
          const active = bucket === b.key;
          const cnt = counts[b.key] || 0;
          return (
            <button key={b.key} onClick={() => setBucket(b.key)}
              className={`text-left rounded-xl border p-4 transition-all ${active ? "bg-[#FF5C00]/[0.08] border-[#FF5C00]/40 ring-2 ring-[#FF5C00]/30" : "bg-white/[0.04] border-white/[0.08] hover:border-white/[0.2]"}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-white/80 font-semibold text-sm">
                  <b.Icon className="w-4 h-4 text-[#FF5C00]" /> {b.label}
                </div>
                {cnt > 0 && <span className="bg-[#FF5C00] text-white text-[10px] font-bold rounded-full min-w-[20px] h-[20px] flex items-center justify-center px-1.5">{cnt}</span>}
              </div>
              <p className="text-[11px] text-white/45 mt-1.5 leading-relaxed">{b.desc}</p>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="space-y-2">{[...Array(3)].map((_, i) => <div key={i} className="h-20 bg-white/[0.04] rounded-lg animate-pulse" />)}</div>
      ) : items.length === 0 ? (
        <div className="bg-white/[0.04] border border-white/[0.1] rounded-xl p-12 text-center">
          <Inbox className="w-10 h-10 text-white/20 mx-auto mb-3" />
          <p className="text-white/50 text-sm">Nothing in this bucket. You're caught up.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map(n => {
            const Icon = kindIcon(n.kind);
            const unread = !n.readAt;
            return (
              <div key={n.id}
                className={`group rounded-xl border p-4 transition-colors flex items-start gap-3 ${unread ? "bg-[#FF5C00]/[0.04] border-[#FF5C00]/25 hover:border-[#FF5C00]/40" : "bg-white/[0.03] border-white/[0.08] hover:border-white/[0.15]"}`}>
                <div className={`w-9 h-9 rounded-lg border flex items-center justify-center flex-shrink-0 ${unread ? "bg-[#FF5C00]/15 border-[#FF5C00]/30 text-[#FF5C00]" : "bg-white/5 border-white/10 text-white/50"}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <button onClick={() => open(n)} className="flex-1 min-w-0 text-left">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className={`text-sm font-medium ${unread ? "text-white" : "text-white/75"}`}>{n.title}</p>
                    <span className="text-[10px] font-mono uppercase tracking-wider text-white/35">{n.kind.replace(/_/g, " ")}</span>
                  </div>
                  {n.body && <p className="text-white/55 text-xs mt-1 leading-relaxed line-clamp-2">{n.body}</p>}
                  <p className="text-white/30 text-[10px] mt-1.5 font-mono">{new Date(n.createdAt).toLocaleString()}</p>
                </button>
                <div className="flex items-center gap-1 flex-shrink-0 opacity-60 group-hover:opacity-100 transition-opacity">
                  {unread && (
                    <button onClick={() => setRead(n.id, true)} title="Mark read" className="text-white/50 hover:text-white p-1">
                      <CheckCheck className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button onClick={() => archive(n.id)} title="Archive" className="text-white/50 hover:text-white p-1">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
